<template>
  <div class="LLTabs2" :class="{ 'LLTabs2--sm': small }" role="tablist" :aria-label="ariaLabel">
    <button
      v-for="(tab, i) in items"
      :key="tab.key || i"
      class="LLTabs2__tab"
      :class="{ LLTabs2__tab_selected: isSelected(tab), LLTabs2__tab_disabled: tab.disabled }"
      :disabled="tab.disabled"
      role="tab"
      :aria-selected="String(isSelected(tab))"
      :tabindex="isSelected(tab) ? 0 : -1"
      @click="onSelect(tab)"
      @keydown.space.prevent="onSelect(tab)"
      @keydown.enter.prevent="onSelect(tab)"
    >
      <span class="LLTabs2__label">
        <slot name="label" :tab="tab">
          {{ tab.label }}
        </slot>
      </span>

      <span v-if="showBadge(tab)" class="LLTabs2__badge" :class="{ LLTabs2__badge_small: tab.badge > 19 }">
        {{ clamp99(tab.badge) }}
      </span>

      <span class="LLTabs2__underline" aria-hidden="true"></span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'LLTabs2',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: [String, Number], default: null },
    items: { type: Array, required: true },
    ariaLabel: { type: String, default: 'Tabs' },
    small: { type: Boolean, default: false }
  },
  methods: {
    isSelected(tab) {
      return (tab.key ?? tab.label) === this.value
    },
    onSelect(tab) {
      if (tab.disabled) return
      const key = tab.key ?? tab.label
      this.$emit('input', key)
      this.$emit('change', key, tab)
    },
    clamp99(n) {
      if (typeof n !== 'number') return n
      return n > 99 ? '99+' : n
    },
    showBadge(tab) {
      return typeof tab.badge === 'number' && tab.badge > 0
    }
  }
}
</script>

<style lang="scss" scoped>
.LLTabs2 {
  $root: &;
  @apply border-b border-neutral-01-25 flex;

  &__tab {
    @apply py-2 flex items-center text-neutral-01-400 ml-6 uppercase outline-none transition duration-200 relative font-semibold;
    font-size: 16px;

    &:first-child {
      @apply ml-0;
    }

    &:after {
      content: '';
    }

    &.LLTabs2__tab_disabled {
      @apply opacity-50 cursor-not-allowed;
    }

    &:not(&_selected):hover #{$root}__underline,
    &:not(&_selected):focus-visible #{$root}__underline {
      @apply bg-neutral-01-400;
    }
  }

  &__badge {
    @apply w-6 h-6 rounded-full bg-neutral-01-400 flex items-center justify-center text-caption-01 text-white ml-2 transition duration-200;
  }

  &__badge_small {
    font-size: 10px;
  }

  &__underline {
    @apply absolute bottom-0 w-full h-0.5 -mb-px transition duration-200;
    left: 0;
  }

  &__tab_selected {
    @apply text-primary-01-300;
    #{$root}__badge {
      @apply bg-primary-01-300;
    }
    #{$root}__underline {
      @apply bg-primary-01-300;
    }
  }

  &.LLTabs2--sm {
    #{$root}__tab {
      @apply py-1 ml-4;
      font-size: 14px;
      &:first-child {
        @apply ml-0;
      }
    }
    #{$root}__badge {
      @apply w-5 h-5 ml-1;
      font-size: 10px;

      &_small {
        font-size: 8px;
      }
      line-height: 1;
    }
    #{$root}__underline {
      @apply h-px;
    }
  }
}
</style>
